import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ClientDashboardTabs from '../../components/navigation/ClientDashboardTabs';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import FloorTypeCard from './components/FloorTypeCard';
import FloorTypeForm from './components/FloorTypeForm';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import BulkActionsBar from './components/BulkActionsBar';
import FloorTypeStats from './components/FloorTypeStats';

// Add this block - Mock companyService since it's not imported
const companyService = {
  getBySlug: async (slug) => {
    // Mock implementation - replace with actual API call
    return { slug, name: 'Mock Company' };
  }
};

const FloorTypesPricing = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [floorTypes, setFloorTypes] = useState([]);
  const [filteredFloorTypes, setFilteredFloorTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showForm, setShowForm] = useState(false);
  const [editingFloorType, setEditingFloorType] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  const categories = [
  'Metallic Epoxy',
  'Flake Epoxy',
  'Countertop Epoxy',
  'Solid Color Epoxy',
  'Custom Design'];


  useEffect(() => {
    // If accessed via company-specific route, load company data
    if (companyIdentifier) {
      const loadCompanyData = async () => {
        try {
          setLoading(true);
          // Extract slug from compound identifier
          const extractSlugFromIdentifier = (identifier) => {
            const parts = identifier?.split('-');
            let uuidStartIndex = -1;
            for (let i = parts?.length - 1; i >= 0; i--) {
              if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
                if (i >= 4 &&
                parts?.[i - 1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 1]) &&
                parts?.[i - 2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 2]) &&
                parts?.[i - 3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 3]) &&
                parts?.[i - 4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i - 4])) {
                  uuidStartIndex = i - 4;
                  break;
                }
              }
            }
            if (uuidStartIndex > 0) {
              return parts?.slice(0, uuidStartIndex)?.join('-');
            }
            return identifier;
          };

          const slug = extractSlugFromIdentifier(companyIdentifier);
          const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();

          const companyData = await companyService?.getBySlug(normalizedSlug);
          setCompany(companyData);
        } catch (error) {
          console.error('Error loading company data:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCompanyData();
    } else {
      setLoading(false);
    }
  }, [companyIdentifier]);

  useEffect(() => {
    const mockFloorTypes = [
    {
      id: 1,
      name: 'Metallic Epoxy Premium',
      description: 'High-gloss metallic finish with stunning visual depth and unique patterns. Perfect for modern residential and commercial spaces seeking a luxurious appearance.',
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d372b1e5-1765222320107.png",
      thumbnailAlt: 'Glossy metallic blue epoxy floor with silver swirls and reflective finish in modern garage space',
      price: '8.50',
      priceUnit: 'per sq ft',
      category: 'Metallic Epoxy',
      isActive: true,
      createdAt: '2025-11-20T10:30:00Z',
      updatedAt: '2025-12-10T14:20:00Z'
    },
    {
      id: 2,
      name: 'Classic Flake System',
      description: 'Durable decorative flake flooring with excellent slip resistance and easy maintenance. Ideal for garages, workshops, and high-traffic commercial areas.',
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_193ee2927-1764798924046.png",
      thumbnailAlt: 'Gray epoxy floor with multicolored decorative flakes in residential garage with tools and storage',
      price: '6.75',
      priceUnit: 'per sq ft',
      category: 'Flake Epoxy',
      isActive: true,
      createdAt: '2025-10-15T09:15:00Z',
      updatedAt: '2025-12-08T11:45:00Z'
    },
    {
      id: 3,
      name: 'Countertop Epoxy Elite',
      description: 'Food-safe epoxy coating designed specifically for kitchen and bathroom countertops. Heat-resistant and stain-proof with crystal-clear finish.',
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9889b36-1765159449018.png",
      thumbnailAlt: 'White marble-effect epoxy countertop with gray veining in modern kitchen with stainless steel appliances',
      price: '12.00',
      priceUnit: 'per sq ft',
      category: 'Countertop Epoxy',
      isActive: true,
      createdAt: '2025-09-05T13:20:00Z',
      updatedAt: '2025-12-12T16:30:00Z'
    },
    {
      id: 4,
      name: 'Solid Color Professional',
      description: 'Single-color epoxy coating with uniform appearance and exceptional durability. Perfect for industrial facilities and commercial warehouses.',
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
      thumbnailAlt: 'Smooth gray solid color epoxy floor in empty commercial warehouse with white walls and fluorescent lighting',
      price: '5.25',
      priceUnit: 'per sq ft',
      category: 'Solid Color Epoxy',
      isActive: true,
      createdAt: '2025-08-22T08:45:00Z',
      updatedAt: '2025-12-05T10:15:00Z'
    },
    {
      id: 5,
      name: 'Custom Artistic Design',
      description: 'Fully customizable epoxy flooring with artistic patterns, logos, or unique color combinations. Tailored to match your brand or personal style.',
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_15ab79267-1765763745028.png",
      thumbnailAlt: 'Colorful abstract pattern epoxy floor with blue, purple, and gold swirls in modern retail showroom',
      price: '15.00',
      priceUnit: 'per sq ft',
      category: 'Custom Design',
      isActive: false,
      createdAt: '2025-07-10T15:30:00Z',
      updatedAt: '2025-11-28T09:20:00Z'
    },
    {
      id: 6,
      name: 'Metallic Copper Blend',
      description: 'Warm copper metallic epoxy with rich bronze undertones. Creates an elegant and sophisticated atmosphere in residential and hospitality settings.',
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1276e026c-1764765799591.png",
      thumbnailAlt: 'Copper metallic epoxy floor with bronze swirls and glossy finish in upscale restaurant dining area',
      price: '9.25',
      priceUnit: 'per sq ft',
      category: 'Metallic Epoxy',
      isActive: true,
      createdAt: '2025-06-18T11:00:00Z',
      updatedAt: '2025-12-11T14:50:00Z'
    }];


    setFloorTypes(mockFloorTypes);
    setFilteredFloorTypes(mockFloorTypes);
  }, []);

  useEffect(() => {
    let filtered = [...floorTypes];

    if (searchTerm) {
      filtered = filtered?.filter(
        (ft) =>
        ft?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        ft?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (filterCategory !== 'all') {
      filtered = filtered?.filter((ft) => ft?.category === filterCategory);
    }

    if (filterStatus !== 'all') {
      filtered = filtered?.filter((ft) =>
      filterStatus === 'active' ? ft?.isActive : !ft?.isActive
      );
    }

    setFilteredFloorTypes(filtered);
  }, [searchTerm, filterCategory, filterStatus, floorTypes]);

  const handleAddNew = () => {
    setEditingFloorType(null);
    setShowForm(true);
  };

  const handleEdit = (floorType) => {
    setEditingFloorType(floorType);
    setShowForm(true);
  };

  const handleDuplicate = (floorType) => {
    const duplicated = {
      ...floorType,
      id: Date.now(),
      name: `${floorType?.name} (Copy)`,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    setFloorTypes((prev) => [...prev, duplicated]);
  };

  const handleDelete = (floorType) => {
    setDeleteConfirmation(floorType);
  };

  const confirmDelete = () => {
    setFloorTypes((prev) => prev?.filter((ft) => ft?.id !== deleteConfirmation?.id));
    setDeleteConfirmation(null);
  };

  const handleToggleStatus = (id) => {
    setFloorTypes((prev) =>
    prev?.map((ft) =>
    ft?.id === id ? { ...ft, isActive: !ft?.isActive, updatedAt: new Date()?.toISOString() } : ft
    )
    );
  };

  const handleSave = (floorTypeData) => {
    if (editingFloorType) {
      setFloorTypes((prev) =>
      prev?.map((ft) => ft?.id === editingFloorType?.id ? floorTypeData : ft)
      );
    } else {
      setFloorTypes((prev) => [...prev, floorTypeData]);
    }
    setShowForm(false);
    setEditingFloorType(null);
  };

  const handleBulkActivate = () => {
    setFloorTypes((prev) =>
    prev?.map((ft) =>
    selectedItems?.includes(ft?.id) ?
    { ...ft, isActive: true, updatedAt: new Date()?.toISOString() } :
    ft
    )
    );
    setSelectedItems([]);
  };

  const handleBulkDeactivate = () => {
    setFloorTypes((prev) =>
    prev?.map((ft) =>
    selectedItems?.includes(ft?.id) ?
    { ...ft, isActive: false, updatedAt: new Date()?.toISOString() } :
    ft
    )
    );
    setSelectedItems([]);
  };

  const handleBulkDelete = () => {
    setFloorTypes((prev) => prev?.filter((ft) => !selectedItems?.includes(ft?.id)));
    setSelectedItems([]);
  };

  const toggleItemSelection = (id) => {
    setSelectedItems((prev) =>
    prev?.includes(id) ? prev?.filter((item) => item !== id) : [...prev, id]
    );
  };

  const categoryOptions = [
  { value: 'all', label: 'All Categories' },
  ...categories?.map((cat) => ({ value: cat, label: cat }))];


  const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' }];


  return (
    <div className="min-h-screen bg-background">
      {companyIdentifier && <ClientDashboardTabs />}
      <div className={companyIdentifier ? "main-content-with-tabs" : ""}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-bold text-text-primary">Floor Types & Pricing</h1>
              <Button
                variant="default"
                onClick={handleAddNew}
                iconName="Plus"
                iconPosition="left"
                iconSize={18}>

                Add Floor Type
              </Button>
            </div>
            <p className="text-text-secondary">
              Manage your epoxy flooring products, pricing, and visual content
            </p>
          </div>

          <FloorTypeStats floorTypes={floorTypes} />

          {showForm ?
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-text-primary">
                  {editingFloorType ? 'Edit Floor Type' : 'Add New Floor Type'}
                </h2>
                <button
                onClick={() => {
                  setShowForm(false);
                  setEditingFloorType(null);
                }}
                className="text-text-secondary hover:text-text-primary transition-colors duration-150">

                  <Icon name="X" size={24} />
                </button>
              </div>
              <FloorTypeForm
              floorType={editingFloorType}
              categories={categories}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingFloorType(null);
              }} />

            </div> :

          <>
              <div className="mt-8 bg-card border border-border rounded-lg p-6">
                <div className="flex flex-col lg:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <Input
                    type="search"
                    placeholder="Search floor types by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)} />

                  </div>
                  <div className="flex gap-3">
                    <Select
                    options={categoryOptions}
                    value={filterCategory}
                    onChange={setFilterCategory}
                    className="w-48" />

                    <Select
                    options={statusOptions}
                    value={filterStatus}
                    onChange={setFilterStatus}
                    className="w-40" />

                    <div className="flex gap-1 border border-border rounded-lg p-1">
                      <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded transition-colors duration-150 ${
                      viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-text-secondary hover:text-text-primary'}`
                      }>

                        <Icon name="Grid3x3" size={18} />
                      </button>
                      <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-colors duration-150 ${
                      viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-text-secondary hover:text-text-primary'}`
                      }>

                        <Icon name="List" size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {filteredFloorTypes?.length === 0 ?
              <div className="text-center py-16">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Grid3x3" size={32} className="text-text-secondary" />
                    </div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      No floor types found
                    </h3>
                    <p className="text-text-secondary mb-6">
                      {searchTerm || filterCategory !== 'all' || filterStatus !== 'all' ? 'Try adjusting your filters or search terms' : 'Get started by adding your first floor type'}
                    </p>
                    {!searchTerm && filterCategory === 'all' && filterStatus === 'all' &&
                <Button
                  variant="default"
                  onClick={handleAddNew}
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={16}>

                        Add Floor Type
                      </Button>
                }
                  </div> :

              <div
                className={
                viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'
                }>

                    {filteredFloorTypes?.map((floorType) =>
                <div key={floorType?.id} className="relative">
                        <div className="absolute top-3 left-3 z-10">
                          <input
                      type="checkbox"
                      checked={selectedItems?.includes(floorType?.id)}
                      onChange={() => toggleItemSelection(floorType?.id)}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-ring cursor-pointer" />

                        </div>
                        <FloorTypeCard
                    floorType={floorType}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus} />

                      </div>
                )}
                  </div>
              }
              </div>
            </>
          }
        </div>
      </div>
      {deleteConfirmation &&
      <DeleteConfirmationModal
        floorType={deleteConfirmation}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirmation(null)} />

      }
      <BulkActionsBar
        selectedCount={selectedItems?.length}
        onActivate={handleBulkActivate}
        onDeactivate={handleBulkDeactivate}
        onDelete={handleBulkDelete}
        onClearSelection={() => setSelectedItems([])} />

    </div>);

};

export default FloorTypesPricing;